import { useEffect, useState } from 'react'
import { apiClient, ApiRequestConfig, ApiResponse } from '../utils/apiClient'

export interface UseApiQueryOptions {
    endpoint: string
    method?: 'GET' | 'POST'
    body?: any
    config?: ApiRequestConfig
    // Used only to know when to re-run the query; not used for caching
    queryKey?: unknown[]
    // Whether the query is enabled
    enabled?: boolean
}

export interface ApiQueryResult<T> {
    data: T | null
    isPending: boolean
    isError: boolean
    error: unknown
    refetch: () => Promise<T | null>
}

export const useApiQuery = <T>({
    endpoint,
    method = 'GET',
    body,
    config,
    queryKey = [],
    enabled = true,
}: UseApiQueryOptions): ApiQueryResult<T> => {
    const [data, setData] = useState<T | null>(null)
    const [isPending, setIsPending] = useState(false)
    const [error, setError] = useState<unknown>(null)

    const serializedKey = JSON.stringify([
        endpoint,
        method,
        body,
        ...(queryKey || []),
    ])

    const fetchData = async (): Promise<T | null> => {
        if (!enabled) {
            return null
        }

        setIsPending(true)
        setError(null)

        try {
            let response: ApiResponse<T>

            if (method === 'POST') {
                response = await apiClient.post<T>(endpoint, body, config)
            } else {
                response = await apiClient.get<T>(endpoint, config)
            }

            setData(response.data)
            return response.data
        } catch (err) {
            setError(err)
            return null
        } finally {
            setIsPending(false)
        }
    }

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, serializedKey])

    return {
        data,
        isPending,
        isError: !!error,
        error,
        refetch: fetchData,
    }
}
